import { useRef, useState, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { DeferredExtendedActionMenu as ExtendedActionMenu } from '~/ui/deferred/deferred-menus';
import { LinkButton } from '@understory-io/leaf/link-button';
import { GiftCardIcon } from '~/ui/icons/gift-card';
import { TicketIcon } from '~/ui/icons/ticket';
import { DateIcon } from '~/ui/icons/date';
import { MenuIcon } from '~/ui/icons/menu';
import { Button } from '@understory-io/leaf/button';
import { localeHref, localeHrefExternal } from '~/lib/use-locale-href';
import { css } from '@understory-io/leaf/css';

export type HeaderMenuFeatures = {
  giftCards: boolean;
  punchcards: boolean;
  privateEventRequest: boolean;
};

type HeaderMenuProps = {
  features?: HeaderMenuFeatures;
  /** Path-param locale (only set when URL is non-default-language prefixed). */
  locale?: string;
};

type ItemDef = {
  key: string;
  href: string;
  label: ReactNode;
  icon: ReactNode;
  description: ReactNode;
};

export function HeaderMenu({ features, locale }: HeaderMenuProps) {
  const intl = useIntl();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const boundaryRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  if (!features) return null;

  const items: ItemDef[] = [];

  if (features.giftCards) {
    items.push({
      key: 'gift-cards',
      href: localeHref(locale, '/:locale?/giftcards'),
      label: (
        <FormattedMessage
          defaultMessage="Buy a gift card"
          description="Header menu item linking to the gift card purchase page"
        />
      ),
      description: (
        <FormattedMessage
          defaultMessage="Make someone happy"
          description="Subtitle on the gift card item in the storefront actions header menu"
        />
      ),
      icon: <GiftCardIcon />,
    });
  }
  if (features.punchcards) {
    items.push({
      key: 'punchcards',
      href: localeHref(locale, '/:locale?/punchcards'),
      label: (
        <FormattedMessage
          defaultMessage="Punch cards"
          description="Header menu item linking to the punch card purchase page"
        />
      ),
      description: (
        <FormattedMessage
          defaultMessage="Going more than once — save with punch cards"
          description="Subtitle on the punch cards item in the storefront actions header menu"
        />
      ),
      icon: <TicketIcon />,
    });
  }
  if (features.privateEventRequest) {
    items.push({
      key: 'private-event',
      href: localeHrefExternal(locale, '/request'),
      label: (
        <FormattedMessage
          defaultMessage="Request a private event"
          description="Header menu item linking to the private event request page"
        />
      ),
      description: (
        <FormattedMessage
          defaultMessage="Custom events for larger groups"
          description="Subtitle on the private event item in the storefront actions header menu"
        />
      ),
      icon: <DateIcon />,
    });
  }

  if (items.length === 0) return null;

  // Gift cards are also surfaced as a dedicated icon button in the header. When
  // it's the only feature, this menu would just duplicate that icon, so hide
  // it. With other features present, gift cards stays listed in the menu too.
  if (items.length === 1 && items[0]!.key === 'gift-cards') return null;

  if (items.length === 1) {
    const item = items[0]!;
    return (
      <LinkButton
        href={item.href}
        appearance="secondary"
        size={{ base: 'small', tablet: 'standard' }}
      >
        {item.label}
      </LinkButton>
    );
  }

  return (
    <>
      <Button
        ref={anchorRef}
        appearance="text"
        // Matches the gift card icon button: a `large`-height text button with
        // the `large-inset` icon, no icon-button recipe.
        className={css({
          color: 'content.secondary',
          height: 'large',
          padding: 'micro',
        })}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-label={intl.formatMessage({
          defaultMessage: 'Open menu',
          description: 'Aria label for the header menu trigger button',
        })}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MenuIcon size="large-inset" />
      </Button>
      {/* Invisible viewport overlay marking the page column the menu must
          stay inside — same horizontal gutter as the header. Leaf bounds
          the menu popover to this element's content box, so long item text
          wraps instead of running the menu into the viewport edge. It must
          span the full viewport height: react-aria also clamps the
          popover's max height to the boundary. */}
      <div
        ref={boundaryRef}
        aria-hidden
        className={css({
          position: 'fixed',
          inset: '[0]',
          paddingInline: { base: 'small', tablet: '2xlarge' },
          pointerEvents: 'none',
          visibility: 'hidden',
        })}
      />
      <ExtendedActionMenu
        anchor={anchorRef}
        open={open}
        onOpenChange={setOpen}
        placement="bottom end"
        boundary={boundaryRef}
        aria-label={intl.formatMessage({
          defaultMessage: 'Storefront actions',
          description:
            'Aria label for the header dropdown listing storefront actions',
        })}
        items={items.map((item) => ({
          label: item.label,
          description: item.description,
          icon: item.icon,
          href: item.href,
        }))}
      />
    </>
  );
}
